import { useMemo } from 'react'
import { Month } from '@mantine/dates'
import { ActionIcon, Button, Group } from '@mantine/core'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import dayjs from 'dayjs'
import type { ScheduleEncounter } from '../service'

const DATE_FORMAT = 'YYYY-MM-DD'

type OverviewCalendarProps = {
  selectedDate: string
  onSelectDate: (date: string) => void
  visibleMonth: string
  onVisibleMonthChange: (month: string) => void
  encounters?: ScheduleEncounter[]
}

export const OverviewCalendar = ({
  selectedDate,
  onSelectDate,
  visibleMonth,
  onVisibleMonthChange,
  encounters = [],
}: OverviewCalendarProps) => {
  const eventDateSet = useMemo(() => {
    return new Set(
      encounters
        .map((encounter) =>
          encounter.periodStart ? dayjs(encounter.periodStart).format(DATE_FORMAT) : null,
        )
        .filter((date): date is string => Boolean(date)),
    )
  }, [encounters])

  const goToPreviousMonth = () => {
    onVisibleMonthChange(dayjs(visibleMonth).subtract(1, 'month').format(DATE_FORMAT))
  }

  const goToNextMonth = () => {
    onVisibleMonthChange(dayjs(visibleMonth).add(1, 'month').format(DATE_FORMAT))
  }

  const goToToday = () => {
    const now = dayjs()
    onSelectDate(now.format(DATE_FORMAT))
    onVisibleMonthChange(now.startOf('month').format(DATE_FORMAT))
  }

  return (
    <div className="home-overview-calendar w-full bg-white px-3 py-2">
      <Group justify="space-between" align="center" mb={4} wrap="nowrap">
        <Group gap={2} wrap="nowrap">
          <ActionIcon
            variant="default"
            bd="1px solid #CCC"
            color="grey"
            size="sm"
            aria-label="Previous month"
            onClick={goToPreviousMonth}
          >
            <IconChevronLeft size={14} />
          </ActionIcon>
          <span className="px-1 text-sm font-bold text-gray-900">
            {dayjs(visibleMonth).format('MMMM YYYY')}
          </span>
        </Group>
        <Group gap={4} wrap="nowrap">
          <ActionIcon
            variant="default"
            bd="1px solid #CCC"
            color="gray"
            size="sm"
            aria-label="Next month"
            onClick={goToNextMonth}
          >
            <IconChevronRight size={14} />
          </ActionIcon>
          <Button size="compact-xs" variant="default" radius="md" onClick={goToToday}>
            <span className="text-[11px] font-normal">Today</span>
          </Button>
        </Group>
      </Group>

      <Month
        month={visibleMonth}
        firstDayOfWeek={0}
        size="sm"
        weekdayFormat="dd"
        getDayProps={(date) => {
          const formatted = dayjs(date).format(DATE_FORMAT)
          return {
            selected: formatted === selectedDate,
            onClick: () => {
              onSelectDate(formatted)
              const clickedMonth = dayjs(date).startOf('month').format(DATE_FORMAT)
              if (clickedMonth !== visibleMonth) {
                onVisibleMonthChange(clickedMonth)
              }
            },
          }
        }}
        renderDay={(date) => {
          const formatted = dayjs(date).format(DATE_FORMAT)
          const day = dayjs(date).date()
          const hasEvent = eventDateSet.has(formatted)
          const isToday = formatted === dayjs().format(DATE_FORMAT)
          // On today the button background is red so the dot must be white to stay visible.
          // When there is no event the dot must be fully transparent regardless of day.
          const dotColor = hasEvent ? (isToday ? 'bg-white' : 'bg-red-500') : 'bg-transparent'
          return (
            <div className="flex flex-col items-center justify-center leading-none">
              <span>{day}</span>
              <span className={`mt-0.5 h-1 w-1 rounded-full ${dotColor}`} />
            </div>
          )
        }}
        styles={{
          weekday: {
            color: 'var(--mantine-color-gray-5)',
            fontSize: 11,
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            padding: '4px 0',
          },
        }}
      />

      <style>{`
        /* Smaller day font to match mockup */
        .home-overview-calendar table button {
          font-size: 11px !important;
        }

        /* Extra x-padding between cells */
        .home-overview-calendar table td {
          padding-left: 6px !important;
          padding-right: 6px !important;
        }

        /* Selected day (not today) — light pink background, red text */
        .home-overview-calendar [data-selected="true"]:not([data-today="true"]),
        .home-overview-calendar [data-selected="true"]:not([data-today="true"]):hover {
          background-color: rgba(220, 38, 38, 0.12) !important;
          color: rgb(220 38 38) !important;
          border-radius: 4px !important;
        }

        /* Today — always solid, white text, regardless of selected state */
        .home-overview-calendar [data-today="true"] {
          background-color: rgb(229, 83, 61) !important;
          color: #fff !important;
          border-radius: 4px !important;
          font-weight: 600;
        }


      `}</style>
    </div>
  )
}
